import axios from 'axios';
import * as UnofficialMessageService from './UnofficialMessageService';
import SessionService from './SessionService';
import { config } from '../config';
import { ensureSparkCompatibleAudio } from '../utils/sparkAudioEncode';

export interface SendReactionDto {
  sectorId: number;
  recipient: string;
  recipientPhone?: string;
  emoji: string;
  messageId?: string;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
}

// Obter a URL da variável de ambiente
const WHATSAPP_API_URL = process.env.REACT_APP_WHATSAPP_API_URL;
const UNOFFICIAL_API_URL = process.env.REACT_APP_UNOFFICIAL_API_URL || 'https://server.mends.pro/api/v1';
// Verificação de segurança para garantir que não estamos usando a porta 5002
const getApiUrl = () => {
  if (!WHATSAPP_API_URL) {
    return 'https://server.mends.pro/api';
  }

  if (WHATSAPP_API_URL.includes(':5002')) {
    console.warn('A porta 5002 foi detectada na URL do WhatsApp API. Removendo...');
    return WHATSAPP_API_URL.replace(':5002', '');
  }

  return WHATSAPP_API_URL;
};

const API_URL = getApiUrl();

const usesUnofficialUazapi = (isSparkContact?: boolean) =>
  !SessionService.getSectorIsOfficial() && !isSparkContact;

// Interface para a resposta da API
export interface MessageResponse {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  senderJid?: string;
  senderName?: string;
  groupJid?: string;
  /** Instagram/Messenger: canal, selo "Instagram · @conta" e estado (deleted | edited). Ausentes no WhatsApp. */
  channel?: 'instagram' | 'messenger' | 'webchat';
  accountLabel?: string;
  /** deleted · edited (Meta) ou a entrega do WhatsApp oficial: sent · delivered · read · played · failed. */
  state?: 'deleted' | 'edited' | 'sent' | 'delivered' | 'read' | 'played' | 'failed';
  /** Motivo em português quando o WhatsApp oficial não entregou a mensagem. */
  deliveryError?: string;
}

export interface GroupMessageResponse {
  id: number;
  sector_id: number;
  group_id: number;
  group_jid: string;
  sender_jid: string;
  sender_name: string;
  content: string;
  media_type: string;
  media_url: string;
  file_name: string;
  mime_type: string;
  is_sent: boolean;
  is_read: boolean;
  whatsapp_message_id: string;
  created_at: string;
}

// Interfaces para as requisições
export interface SendMessageDto {
  to: string;
  recipientPhone: string;
  text: string;
  contactId: number;
  sectorId: number;
  userId: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  isSparkContact?: boolean;
}

// Constantes para tipos de áudio suportados
const SUPPORTED_AUDIO_TYPES = ['audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/wav'];

export interface SendFileDto {
  file?: File;
  base64File?: string;
  fileName: string;
  mediaType: string;
  caption?: string;
  recipient: string;
  recipientPhone: string;
  contactId: number;
  sectorId: number;
  isHuman?: boolean;
  userId?: number;
  isAnonymous?: boolean;
  sentAt?: string;
  timezone?: string;
  isSparkContact?: boolean;
}

export interface MessageType {
  id: number;
  content: string | null;
  isSent: boolean;
  mediaType?: string;
  mediaUrl?: string | null;
  fileName?: string;
  sectorId?: number;
  contactID: number;
  isRead: boolean;
  sentAt?: string;
  attachment?: {
    url: string;
    type: string;
    name: string;
  };
}

// Interface para a nova resposta da API de mensagens
interface ContactMessageApiItem {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactId: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  senderName?: string;
  repliedToId?: number;
  repliedContent?: string;
  repliedSender?: string;
  /** Mensagem da Meta: instagram | messenger. Nulo = WhatsApp. */
  channel?: string | null;
  accountLabel?: string | null;
  /** deleted | edited | sent | delivered | read | played | failed */
  state?: string | null;
  deliveryError?: string | null;
}

interface GetMessagesApiResponse {
  status: string;
  message: string;
  data: {
    messages: ContactMessageApiItem[];
    total: number;
    hasMore: boolean;
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
      nextOffset: number;
    };
    contact: {
      id: number;
      name: string;
      number: string;
      avatarUrl: string;
      sparkChatId?: string | null;
      sparkEntrypointId?: string | null;
      sparkLinePhone?: string | null;
      sparkLineLabel?: string | null;
      channel?: string | null;
      SparkChatId?: string | null;
      SparkEntrypointId?: string | null;
      SparkLinePhone?: string | null;
      SparkLineLabel?: string | null;
      Channel?: string | null;
    };
  };
  timestamp: string;
}

// Interface para o retorno da função com informações de paginação
export interface GetMessagesResult {
  messages: MessageResponse[];
  hasMore: boolean;
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
    nextOffset: number;
  };
  contact?: GetMessagesApiResponse['data']['contact'];
}

const CONTACTS_API = `${config.UNOFFICIAL_API_URL}/contacts`;

const mapContactMessage = (m: ContactMessageApiItem, contactId: number): MessageResponse => ({
  id: m.id,
  content: m.content,
  mediaType: (m.mediaType || 'text').toLowerCase(),
  mediaUrl: m.mediaUrl,
  fileName: m.fileName,
  mimeType: m.mimeType,
  sectorId: m.sectorId,
  contactID: m.contactId ?? contactId,
  sentAt: m.sentAt,
  isSent: m.isSent,
  isRead: m.isRead,
  channel: m.channel === 'instagram' || m.channel === 'messenger' ? m.channel : undefined,
  accountLabel: m.accountLabel || undefined,
  state: ['deleted', 'edited', 'sent', 'delivered', 'read', 'played', 'failed'].includes(m.state ?? '')
    ? (m.state as MessageResponse['state'])
    : undefined,
  deliveryError: m.deliveryError || undefined,
});

const fetchContactMessagesPage = async (
  contactId: number,
  limit: number,
  offset: number
): Promise<GetMessagesResult> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await axios.get<GetMessagesApiResponse>(
    `${CONTACTS_API}/${contactId}/messages`,
    {
      params: { limit, offset },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    }
  );

  const page = response.data.data;
  const messages = (page.messages ?? []).map((m) => mapContactMessage(m, contactId));

  return {
    messages,
    hasMore: page.hasMore,
    total: page.total,
    pagination: {
      limit: page.pagination.limit,
      offset: page.pagination.offset,
      total: page.pagination.total,
      hasMore: page.pagination.hasMore,
      nextOffset: page.pagination.nextOffset,
    },
    contact: page.contact,
  };
};

export const getMessagesByContactId = async (
  contactId: number,
  limit: number = 10,
  offset: number = 0
): Promise<MessageResponse[]> => {
  const page = await fetchContactMessagesPage(contactId, limit, offset);
  return page.messages;
};

export const getMessagesByContactIdWithPagination = async (
  contactId: number,
  limit: number = 10,
  offset: number = 0
): Promise<GetMessagesResult> => fetchContactMessagesPage(contactId, limit, offset);

// Função para buscar mensagens de um grupo
export const getGroupMessages = async (
  sectorId: number,
  groupId: number,
  limit: number = 50,
  offset: number = 0
): Promise<GroupMessageResponse[]> => {
  try {
    const response = await axios.get<{ success: boolean; data: GroupMessageResponse[] }>(
      `${API_URL}/whatsapp-groups/${sectorId}/${groupId}/messages`,
      {
        params: { limit, offset },
        headers: {
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );

    if (response.data && response.data.success) {
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error("Erro ao buscar mensagens do grupo:", error);
    return [];
  }
};

// Função para enviar mensagem de texto
export const sendMessage = async (message: SendMessageDto): Promise<MessageResponse> => {
  try {
    if (!message.to || !message.text || !message.recipientPhone) {
      throw new Error('Required fields are missing');
    }

    if (usesUnofficialUazapi(message.isSparkContact)) {
      const unofficialResponse = await UnofficialMessageService.sendTextMessage({
        sectorId: message.sectorId,
        recipient: message.recipientPhone,
        message: message.text,
        contactId: message.contactId,
        userId: message.userId,
        isAnonymous: message.isAnonymous,
        sentAt: message.sentAt || new Date().toISOString(),
        ...(message.timezone ? { timezone: message.timezone } : {})
      });

      if (!unofficialResponse || unofficialResponse.status === 'error') {
        throw new Error(unofficialResponse?.message || 'Error sending message');
      }

      return {
        id: Date.now(),
        content: message.text,
        mediaType: 'text',
        mediaUrl: null,
        fileName: null,
        mimeType: null,
        sectorId: message.sectorId,
        contactID: message.contactId,
        sentAt: unofficialResponse.timestamp || message.sentAt || new Date().toISOString(),
        isSent: unofficialResponse.status === 'success',
        isRead: false
      };
    }

    const payload = {
      to: message.to,
      recipientPhone: message.recipientPhone,
      text: message.text,
      contactId: message.contactId,
      sectorId: message.sectorId,
      userId: message.userId,
      isAnonymous: message.isAnonymous,
      sentAt: message.sentAt || new Date().toISOString()
    };

    console.log(`Enviando mensagem para: ${API_URL}/whatsapp/send-message`);
    const response = await axios.post<MessageResponse>(`${API_URL}/whatsapp/send-message`, payload, {
      headers: { Authorization: `Bearer ${SessionService.getToken()}` }
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao enviar mensagem:", error);
    throw error;
  }
};

// Função para enviar mensagem de texto para grupo
// Now delegates to unified sendMessage (ensures it hits /whatsapp/send-message for official path
// or unofficial, and supports recipient as group JID; sector access enforced server-side).
export const sendGroupMessage = async (
  sectorId: number,
  recipient: string,
  message: string,
  userId?: number,
  isAnonymous?: boolean
): Promise<any> => {
  return sendMessage({
    to: recipient,
    recipientPhone: recipient,
    text: message,
    contactId: 0,
    sectorId,
    userId: userId || 0,
    isAnonymous: !!isAnonymous
  });
};

// Função para enviar arquivo
export async function sendFile(fileData: SendFileDto): Promise<MessageResponse> {
  try {
    if (usesUnofficialUazapi(fileData.isSparkContact)) {
      let base64Data: string;
      let fileName: string;
      let mediaType: string;

      // Determina se é um arquivo base64 ou File
      if (fileData.base64File) {
        base64Data = fileData.base64File;
        fileName = fileData.fileName;
        mediaType = fileData.mediaType;
      } else if (fileData.file) {
        base64Data = await fileToBase64(fileData.file);
        fileName = fileData.file.name;
        mediaType = fileData.file.type;
      } else {
        throw new Error('Either base64File or file must be provided');
      }

      const isImage = mediaType.startsWith('image/');
      const isAudio = SUPPORTED_AUDIO_TYPES.some(type => mediaType.startsWith(type) || mediaType === type);
      const isDocument = !isImage && !isAudio;

      // Se for áudio, garantir que está em um formato suportado
      if (isAudio && !SUPPORTED_AUDIO_TYPES.includes(mediaType)) {
        throw new Error(`Unsupported audio format. Supported formats are: ${SUPPORTED_AUDIO_TYPES.join(', ')}`);
      }

      let response;
      if (isImage) {
        response = await UnofficialMessageService.sendImage({
          base64File: base64Data,
          fileName,
          mediaType,
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString()
        });
      } else if (isDocument) {
        response = await UnofficialMessageService.sendDocument({
          base64File: base64Data,
          fileName,
          mediaType,
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString()
        });
      } else {
        // Para áudio, sempre usar audio/mpeg como tipo
        response = await UnofficialMessageService.sendAudio({
          base64File: base64Data,
          fileName,
          mediaType: 'audio/mpeg',
          recipient: fileData.recipientPhone,
          sectorId: fileData.sectorId,
          caption: fileData.caption || '',
          userId: fileData.userId,
          isAnonymous: fileData.isAnonymous,
          sentAt: fileData.sentAt || new Date().toISOString()
        });
      }

      if (response.status === 'error') {
        throw new Error(response.message || 'Failed to send file');
      }

      return {
        id: Date.now(),
        content: fileData.caption || null,
        mediaType: isImage ? 'image' : isAudio ? 'audio' : 'document',
        mediaUrl: null,
        fileName: fileName || null,
        mimeType: isAudio ? 'audio/mpeg' : mediaType || null,
        sectorId: fileData.sectorId,
        contactID: fileData.contactId,
        sentAt: response.timestamp || fileData.sentAt || new Date().toISOString(),
        isSent: response.status === 'success',
        isRead: false
      };
    }

    // Envio oficial
    if (!fileData.file) {
      throw new Error('File is required for official sending');
    }

    const formData = new FormData();
    formData.append('file', fileData.file);
    formData.append('caption', fileData.caption || '');
    formData.append('recipient', fileData.recipient);
    formData.append('recipientPhone', fileData.recipientPhone);
    formData.append('contactId', fileData.contactId.toString());
    formData.append('sectorId', fileData.sectorId.toString());
    if (fileData.isHuman !== undefined) {
      formData.append('isHuman', fileData.isHuman.toString());
    }
    if (fileData.userId !== undefined) {
      formData.append('userId', String(fileData.userId));
    }
    if (fileData.isAnonymous !== undefined) {
      formData.append('isAnonymous', String(fileData.isAnonymous));
    }
    formData.append('sentAt', fileData.sentAt || new Date().toISOString());

    const response = await axios.post(
      `${getApiUrl()}/whatsapp/send-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );

    return response.data;

  } catch (error) {
    console.error('Error sending file:', error);
    throw error;
  }
}

// Função para converter WEBM para WAV
const convertWebmToWav = async (webmBlob: Blob): Promise<Blob> => {
  return new Promise(async (resolve, reject) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const arrayBuffer = await webmBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const wavBuffer = audioBufferToWav(audioBuffer);
      const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      resolve(wavBlob);
    } catch (error) {
      console.error('Erro na conversão de áudio:', error);
      reject(error);
    }
  });
};

// Função para converter AudioBuffer para WAV
const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numOfChan = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numOfChan * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = buffer.length * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;
  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  // FMT sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);

  // Data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write the PCM samples
  const channels = [];
  let offset = 44;
  for (let i = 0; i < buffer.numberOfChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      let sample = Math.max(-1, Math.min(1, channels[channel][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }

  return arrayBuffer;
};

const writeString = (view: DataView, offset: number, string: string): void => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

// Função para enviar áudio
export const sendAudioMessage = async (
  audioBlob: Blob,
  recipient: string,
  contactId: number,
  sectorId: number,
  userId?: number,
  isAnonymous?: boolean,
  sentAt?: string,
  /** Contato Spark: usa /send-audio (gateway roteia audioId). Não altera path Uazapi. */
  isSparkContact?: boolean
): Promise<MessageResponse> => {
  try {
    console.log('Áudio original:', {
      type: audioBlob.type,
      size: audioBlob.size
    });

    const sent = sentAt || new Date().toISOString();

    // Spark / WhatsApp Cloud: só MP3/OGG/AAC/M4A. WebM/WAV do recorder → MP3.
    if (isSparkContact) {
      const prepared = await ensureSparkCompatibleAudio(audioBlob);
      const base64Audio = await blobToBase64(prepared.blob);

      await axios.post(
        `${API_URL}/whatsapp/send-audio`,
        {
          recipient,
          recipientPhone: recipient,
          to: recipient,
          base64File: base64Audio,
          fileName: prepared.fileName,
          mediaType: prepared.mimeType,
          contactId,
          sectorId,
          userId,
          isAnonymous,
          sentAt: sent,
          caption: ''
        },
        { headers: { Authorization: `Bearer ${SessionService.getToken()}` } }
      );

      return {
        id: Date.now(),
        content: null,
        mediaType: 'audio',
        mediaUrl: null,
        fileName: prepared.fileName,
        mimeType: prepared.mimeType,
        sectorId,
        contactID: contactId,
        sentAt: sent,
        isSent: true,
        isRead: false
      };
    }

    // Converter WEBM para WAV (path Uazapi / Meta)
    const wavBlob = await convertWebmToWav(audioBlob);
    const base64Audio = await blobToBase64(wavBlob);
    const fileName = `audio_${Date.now()}.wav`;

    if (usesUnofficialUazapi(isSparkContact)) {
      const unofficialResponse = await UnofficialMessageService.sendAudio({
        base64File: base64Audio,
        fileName,
        mediaType: 'audio/wav',
        recipient,
        sectorId,
        caption: '',
        userId,
        isAnonymous,
        sentAt: sent,
        contactId
      });

      if (unofficialResponse.status === 'error') {
        throw new Error(unofficialResponse.message || 'Error sending audio');
      }

      return {
        id: Date.now(),
        content: null,
        mediaType: 'audio',
        mediaUrl: null,
        fileName,
        mimeType: 'audio/wav',
        sectorId,
        contactID: contactId,
        sentAt: unofficialResponse.timestamp || sent,
        isSent: unofficialResponse.status === 'success',
        isRead: false
      };
    }

    const formData = new FormData();
    formData.append('file', wavBlob, fileName);
    formData.append('caption', '');
    formData.append('recipient', recipient);
    formData.append('contactId', contactId.toString());
    formData.append('sectorId', sectorId.toString());
    if (userId !== undefined) {
      formData.append('userId', String(userId));
    }
    if (isAnonymous !== undefined) {
      formData.append('isAnonymous', String(isAnonymous));
    }
    formData.append('sentAt', sent);

    const response = await axios.post<MessageResponse>(
      `${getApiUrl()}/whatsapp/send-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${SessionService.getToken()}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Erro detalhado ao enviar áudio:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

// Funções auxiliares
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        try {
          if (typeof reader.result === 'string') {
            const base64Data = reader.result.split(',')[1];
            if (!base64Data) {
              reject(new Error('Failed to extract base64 data'));
              return;
            }
            resolve(base64Data);
          } else {
            reject(new Error('Invalid file data'));
          }
        } catch (error) {
          reject(new Error('Error processing file data'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
    } catch (error) {
      reject(new Error('Error initializing file reader'));
    }
  });
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result as string;
        // Qualquer data-URL (webm/ogg/mp3/…); não deixar o prefixo corromper o upload Spark.
        const cleanBase64 = base64String.includes(',')
          ? base64String.slice(base64String.indexOf(',') + 1)
          : base64String;
        resolve(cleanBase64);
      } catch (error) {
        reject(new Error('Error processing audio to base64'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading audio file'));
    reader.readAsDataURL(blob);
  });
};

export const determineMediaType = (file: File): string => {
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const audioTypes = SUPPORTED_AUDIO_TYPES;

  if (imageTypes.includes(file.type)) {
    return 'image';
  }
  if (audioTypes.includes(file.type)) {
    return 'audio';
  }
  return 'document';
};

export const normalizeMessage = (message: MessageType): MessageType => {
  if (!message.mediaUrl && !message.attachment?.url) {
    return message;
  }

  if (message.mediaType === 'image' || message.mediaType === 'audio' || message.mediaType === 'voice') {
    const resolvedUrl = message.mediaUrl || message.attachment?.url || null;
    return {
      ...message,
      mediaUrl: resolvedUrl || message.mediaUrl,
      attachment: undefined
    };
  }

  if (message.mediaType === 'document' || message.attachment) {
    return {
      ...message,
      mediaUrl: null,
      attachment: {
        type: 'document',
        url: message.mediaUrl || message.attachment?.url || '',
        name: message.fileName || message.attachment?.name || 'arquivo'
      }
    };
  }

  return message;
};

// Send reaction (emote) - wires to C# /whatsapp/send-reaction for official, or unofficial service.
// Supports group JIDs in recipient as well.
export const sendReaction = async (dto: SendReactionDto): Promise<MessageResponse> => {
  try {
    const recipient = dto.recipient || dto.recipientPhone || '';
    const isOfficial = SessionService.getSectorIsOfficial();

    if (!isOfficial) {
      const unofficialResponse = await UnofficialMessageService.sendReaction({
        sectorId: dto.sectorId,
        recipient,
        emoji: dto.emoji,
        messageId: dto.messageId,
        userId: dto.userId,
        isAnonymous: dto.isAnonymous,
        sentAt: dto.sentAt || new Date().toISOString()
      });

      if (unofficialResponse && unofficialResponse.status === 'error') {
        throw new Error(unofficialResponse.message || 'Error sending reaction');
      }

      return {
        id: Date.now(),
        content: dto.emoji,
        mediaType: 'reaction',
        mediaUrl: null,
        fileName: null,
        mimeType: null,
        sectorId: dto.sectorId,
        contactID: 0,
        sentAt: unofficialResponse?.timestamp || dto.sentAt || new Date().toISOString(),
        isSent: !unofficialResponse || unofficialResponse.status !== 'error',
        isRead: false
      };
    }

    // Official path: call the new endpoint in WhatsappIntegrationController
    const payload = {
      sectorId: dto.sectorId,
      recipient,
      recipientPhone: recipient,
      messageId: dto.messageId || '',
      emoji: dto.emoji,
      reaction: dto.emoji
    };

    const response = await axios.post(`${API_URL}/whatsapp/send-reaction`, payload, {
      headers: {
        Authorization: `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar reação:', error);
    throw error;
  }
};

// Função para buscar contatos
export const getWhatsAppContacts = async (sectorId: number): Promise<any[]> => {
  try {
    const base = (process.env.REACT_APP_API_URL || 'https://server.mends.pro/api').replace(/\/$/, '');
    const response = await axios.get(`${base}/whatsapp/contact/${sectorId}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("Erro ao buscar contatos:", error);
    return [];
  }
};



